import type { TFunction } from 'i18next';
import { formatFileSize, formatLocalDateTime } from './format';

/** 后端 GraphQL 错误的扩展字段（GraphQLExceptionHandler） */
export interface WebsiteContentErrorInfo {
    code?: string;
    messageKey?: string;
    details: Record<string, unknown>;
}

/** 全局过滤器拦截请求时返回 HTTP 400 与这两个错误码（XssSqlHtmlFilterConfig） */
const FILTER_BLOCKED_CODES = ['XSS_DETECTED', 'SQL_INJECTION_DETECTED'];

/** 由全局 errorLink 统一提示的错误码，页面不再重复提示 */
const GLOBALLY_HANDLED_CODES = ['UNAUTHORIZED', 'PERMISSION', 'INTERNAL_ERROR'];

export function extractErrorInfo(e: unknown): WebsiteContentErrorInfo | null {
    const extensions = (e as any)?.graphQLErrors?.[0]?.extensions;
    if (!extensions) return null;
    return {
        code: extensions.code as string | undefined,
        messageKey: extensions.messageKey as string | undefined,
        details: (extensions.details as Record<string, unknown> | undefined) ?? {},
    };
}

/** 请求被全局 XSS / SQL 过滤器拦截（如文件名含 select … from） */
export function isBlockedByFilter(e: unknown): boolean {
    const networkError = (e as any)?.networkError;
    return networkError?.statusCode === 400 && FILTER_BLOCKED_CODES.includes(networkError?.result?.code);
}

/** 发布被拒时缺失的项，如 `EN.title`、`coverAlt`；不是完整性错误时返回空数组 */
export function extractMissingFields(e: unknown): string[] {
    const info = extractErrorInfo(e);
    if (info?.messageKey !== 'errors.validation.websiteContent.incomplete') return [];
    const missing = info.details.missing;
    return Array.isArray(missing) ? missing.map(String) : [];
}

/** 编辑冲突：打开或上次保存后有人（含本人在另一个窗口）保存过这篇内容 */
export interface EditConflict {
    updatedByName: string | null;
    updatedAt: string | null;
}

/** 保存、发布、定时返回编辑冲突时取最后修改人与时间；不是冲突时返回 null */
export function extractEditConflict(e: unknown): EditConflict | null {
    const info = extractErrorInfo(e);
    if (info?.messageKey !== 'errors.business.websiteContent.editConflict') return null;
    const { updatedByName, updatedAt } = info.details;
    return {
        updatedByName: updatedByName ? String(updatedByName) : null,
        updatedAt: updatedAt ? String(updatedAt) : null,
    };
}

/** 编辑冲突的提示文案，如「Bob 于 2026-11-03 15:00 修改过这篇内容」 */
export const describeEditConflict = (conflict: EditConflict, t: TFunction): string =>
    String(t('websiteContent.errors.editConflict', {
        name: conflict.updatedByName ?? String(t('websiteContent.errors.someone')),
        time: formatLocalDateTime(conflict.updatedAt),
    }));

const fieldLabel = (field: unknown, t: TFunction): string => {
    const raw = String(field ?? '');
    // localizations.EN.bodyJson、attachments[0].displayName 等取最后一段作为字段名
    const name = raw.replace(/\[\d+\]/g, '').split('.').pop() || raw;
    return String(t(`websiteContent.fields.${name}`, { defaultValue: raw }));
};

const langLabel = (lang: unknown, t: TFunction): string =>
    lang ? String(t(`websiteContent.lang.${String(lang)}`, { defaultValue: String(lang) })) : '';

/** 字段后的语言说明，如「标题（西语）」；不分语言的字段为空 */
const langSuffix = (lang: unknown, t: TFunction): string =>
    lang ? String(t('websiteContent.errors.langSuffix', { lang: langLabel(lang, t) })) : '';

/**
 * 把官网内容、官网媒体接口的错误转成提示文案，按 details 的 reason、field、status + action 细化。
 * 返回 null 表示已由全局 errorLink 提示（网络错误、未登录、无权限、系统错误），页面不要再提示。
 */
export function resolveWebsiteContentError(e: unknown, t: TFunction): string | null {
    if (isBlockedByFilter(e)) return String(t('websiteContent.errors.blockedByFilter'));
    const info = extractErrorInfo(e);
    if (!info) return null;
    if (info.code && GLOBALLY_HANDLED_CODES.includes(info.code)) return null;
    const { messageKey, details } = info;
    const reason = details.reason ? String(details.reason) : '';
    const generic = String(t('websiteContent.errors.operationFailed'));
    switch (messageKey) {
        case 'errors.validation.websiteContent.invalidField':
            return String(t(`websiteContent.errors.invalidField.${reason}`, {
                defaultValue: String(t('errors.validation.websiteContent.invalidField', { defaultValue: generic })),
                field: fieldLabel(details.field, t),
                lang: langSuffix(details.lang, t),
                max: details.max,
                min: details.min,
                publishDate: details.publishDate,
            }));
        case 'errors.validation.websiteContent.bodyInvalid':
            return String(t('websiteContent.errors.bodyInvalid', {
                lang: langLabel(details.lang, t),
                reason: String(t(`websiteContent.errors.bodyReason.${reason}`, { defaultValue: reason })),
            }));
        case 'errors.validation.websiteContent.mediaUnavailable':
            return String(t('websiteContent.errors.mediaUnavailable', { field: fieldLabel(details.field, t) }));
        case 'errors.validation.websiteContent.incomplete': {
            const missing = Array.isArray(details.missing) ? details.missing.length : 0;
            return String(t('websiteContent.errors.incomplete', { count: missing }));
        }
        case 'errors.business.websiteContent.statusNotAllowed':
            return String(t('websiteContent.errors.statusNotAllowed', {
                status: String(t(`websiteContent.status.${String(details.status ?? '')}`, {
                    defaultValue: String(details.status ?? ''),
                })),
                action: String(t(`websiteContent.errors.action.${String(details.action ?? '')}`, {
                    defaultValue: String(details.action ?? ''),
                })),
            }));
        case 'errors.business.websiteContent.editConflict':
            return describeEditConflict(extractEditConflict(e) as EditConflict, t);
        case 'errors.business.websiteMedia.inUse':
            return String(t('websiteContent.errors.mediaInUse', { count: Number(details.count ?? 0) }));
        case 'errors.validation.websiteMedia.unsupported':
        case 'errors.validation.websiteMedia.invalidFile':
            return String(t(`websiteContent.errors.mediaReason.${reason}`, {
                defaultValue: String(t(messageKey, { defaultValue: generic })),
                max: details.max,
                maxSize: formatFileSize(details.maxBytes as number | undefined),
            }));
        default: {
            if (!messageKey) return generic;
            const translated = String(t(messageKey, { defaultValue: '' }));
            return translated && translated !== messageKey ? translated : generic;
        }
    }
}

/** 缺失项（如 `ES.title`、`coverAlt`）的可读名称，如「标题（西语）」 */
export const describeMissingField = (field: string, t: TFunction): string => {
    const [first, second] = field.split('.');
    if (second) {
        return String(t('websiteContent.editor.fieldWithLang', { field: fieldLabel(second, t), lang: langLabel(first, t) }));
    }
    return fieldLabel(first, t);
};

/**
 * 定时发布退回草稿的原因（服务端写入主表，格式为「错误消息键|细节」）转成提示文字：
 * 缺失项列出名称，字段错误按原因，正文错误带语言；无法识别时退回通用说明。
 */
export function describeScheduleFailure(reason: string, t: TFunction): string {
    const [messageKey, detail = ''] = reason.split('|');
    switch (messageKey) {
        case 'errors.validation.websiteContent.incomplete':
            return String(t('websiteContent.scheduleFailure.incomplete', {
                items: detail.split(',').filter(Boolean).map((field) => describeMissingField(field, t))
                    .join(String(t('websiteContent.editor.listSeparator'))),
            }));
        case 'errors.validation.websiteContent.invalidField': {
            const [field, fieldReason] = detail.split(':');
            return String(t(`websiteContent.errors.invalidField.${fieldReason}`, {
                defaultValue: String(t(messageKey)),
                field: fieldLabel(field, t),
                lang: '',
                max: '',
                publishDate: '',
            }));
        }
        case 'errors.validation.websiteContent.bodyInvalid': {
            const [lang, bodyReason] = detail.split(':');
            return String(t('websiteContent.errors.bodyInvalid', {
                lang: langLabel(lang, t),
                reason: String(t(`websiteContent.errors.bodyReason.${bodyReason}`, { defaultValue: bodyReason ?? '' })),
            }));
        }
        case 'errors.validation.websiteContent.mediaUnavailable':
            return String(t('websiteContent.errors.mediaUnavailable', { field: fieldLabel(detail.split(':')[0], t) }));
        default:
            return String(t(messageKey, { defaultValue: String(t('websiteContent.scheduleFailure.unknown')) }));
    }
}
